import React, { useState } from "react";
import PageHeader from "@/components/PageHeader";
import { motion } from "framer-motion";
import { Mail, Phone, MapPin, MessageCircle, ArrowRight, Loader2, Check } from "lucide-react";
import axios from "axios";
import { WHATSAPP_NUMBER } from "@/data/content";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const OFFICES = [
  { city: "Delhi", address: "First Floor K 394 Street No 8, Mahipalpur Extension, New Delhi, 07-Delhi" , phone: "+91 96224 69097"},
  { city: "Patna",  address: "Prachi Aashirwad Apartment, Exhibition Rd, Ali Nagar Colony, Salimpur Ahra, Dujra Diara, Patna, Bihar 800001", phone: "+91 96224 69097" },
  { city: "Ghaziabad",  address: "D-617, Savy Ville De, Rajnagar Extension, Ghaziabad, Uttar Pradesh, 201003", phone: "+91 96224 69097" },
  { city: "Jaipur",  address: "3, Jacob Rd, near Tandon Eye Center, Mysore House/Achrol House Colony, Madrampur, Civil Lines, Jaipur, Rajasthan 302006", phone: "+91 96224 69097" },
  { city: "Goa",  address: "Sangolda, Goa, 403521", phone: "+91 96224 69097" },

];

export default function ContactPage() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "", destination: "General Enquiry" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setErr("");
    try {
      await axios.post(`${API}/leads`, form);
      setDone(true);
    } catch (e2) {
      setErr("Could not submit. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <PageHeader
        overline="Say Hello"
        title={"Let's plan something\nunforgettable."}
        subtitle="Drop us a note, WhatsApp a consultant, or walk into one of our offices. We respond to every enquiry within 24 hours."
        image="https://images.unsplash.com/photo-1507525428034-b723cf961d3e?q=80&w=2000&auto=format&fit=crop"
      />

      <section className="py-24 md:py-32 px-6 lg:px-10 bg-white">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-16">
          {/* Left: contact form */}
          <div>
            <div className="overline">Send an enquiry</div>
            <h2 className="font-display text-4xl md:text-5xl text-[#0A2342] leading-[1.05] mt-3">
              Tell us about your journey.
            </h2>
            <p className="text-[#6B7280] mt-4 max-w-lg">A senior consultant reaches out within 24 hours with a personalised proposal — at no cost, no obligation.</p>

            {done ? (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mt-8 p-8 rounded-3xl bg-[#F8F9FB] border border-gray-100"
                data-testid="contact-success"
              >
                <div className="w-14 h-14 rounded-full bg-[#C9A227]/15 flex items-center justify-center">
                  <Check className="w-6 h-6 text-[#C9A227]" />
                </div>
                <h3 className="font-display text-2xl text-[#0A2342] mt-5">Message received.</h3>
                <p className="text-[#6B7280] mt-2">Our team will respond within 24 hours. Meanwhile, feel free to explore our destinations or WhatsApp us for anything urgent.</p>
              </motion.div>
            ) : (
              <form onSubmit={submit} className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="contact-form">
                <Field label="Full name" value={form.name} onChange={set("name")} required testid="contact-name" />
                <Field label="Email" type="email" value={form.email} onChange={set("email")} required testid="contact-email" />
                <Field label="Phone" value={form.phone} onChange={set("phone")} testid="contact-phone" />
                <Field label="Destination / Interest" value={form.destination} onChange={set("destination")} testid="contact-destination" />
                <div className="md:col-span-2">
                  <label className="block text-xs uppercase tracking-[0.18em] text-[#6B7280] mb-2">Message</label>
                  <textarea
                    rows={5}
                    value={form.message}
                    onChange={set("message")}
                    data-testid="contact-message"
                    className="w-full bg-[#F8F9FB] border border-gray-200 rounded-2xl px-4 py-3"
                    placeholder="Anything we should know…"
                  />
                </div>
                {err && <div className="md:col-span-2 text-sm text-red-500">{err}</div>}
                <button type="submit" disabled={loading} data-testid="contact-submit" className="btn-gold md:col-span-2 justify-center">
                  {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending…</> : <>Send Enquiry <ArrowRight className="w-4 h-4" /></>}
                </button>
              </form>
            )}
          </div>

          {/* Right: info */}
          <div className="space-y-8">
            <div className="lux-card p-8">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-2xl bg-[#F8F9FB] flex items-center justify-center shrink-0">
                  <Mail className="w-5 h-5 text-[#C9A227]" />
                </div>
                <div>
                  <div className="overline">Email</div>
                  <a href="mailto:info@travwave.in" className="font-display text-xl text-[#0A2342] block mt-1 hover:text-[#C9A227]">info@travwave.in</a>
                  <div className="text-sm text-[#6B7280] mt-1">Replies within 24 hours.</div>
                </div>
              </div>
            </div>
            <div className="lux-card p-8">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-2xl bg-[#F8F9FB] flex items-center justify-center shrink-0">
                  <MessageCircle className="w-5 h-5 text-[#C9A227]" />
                </div>
                <div>
                  <div className="overline">Message Us</div>
                  <a href={`https://wa.me/${WHATSAPP_NUMBER}`} target="_blank" rel="noopener noreferrer" className="font-display text-xl text-[#0A2342] block mt-1 hover:text-[#C9A227]">Chat with a consultant</a>
                  <div className="text-sm text-[#6B7280] mt-1">Mon–Sat · 9am–9pm IST</div>
                </div>
              </div>
            </div>
            <div className="lux-card p-8">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-2xl bg-[#F8F9FB] flex items-center justify-center shrink-0">
                  <Phone className="w-5 h-5 text-[#C9A227]" />
                </div>
                <div>
                  <div className="overline">Phone</div>
                  <a href="tel:+919622469097" className="font-display text-xl text-[#0A2342] block mt-1 hover:text-[#C9A227]">+91 96224 69097</a>
                  <div className="text-sm text-[#6B7280] mt-1">24×7 travel assistance line.</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Offices */}
        <div className="max-w-7xl mx-auto mt-24">
          <div className="flex items-center gap-3 mb-4">
            <span className="w-10 h-px bg-[#C9A227]" />
            <span className="overline">Our Offices</span>
          </div>
          <h2 className="font-display text-4xl md:text-5xl text-[#0A2342] leading-[1.05]">Walk in, tea's on us.</h2>
          <div className="mt-10 grid md:grid-cols-3 gap-5">
            {OFFICES.map((o) => (
              <div key={o.city} className="lux-card p-8">
                <MapPin className="w-5 h-5 text-[#C9A227]" />
                <div className="font-display text-2xl text-[#0A2342] mt-3">{o.city}</div>
                <div className="text-sm text-[#6B7280] mt-2 leading-relaxed">{o.address}</div>
                <div className="text-sm text-[#0A2342] mt-3">{o.phone}</div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}

function Field({ label, type = "text", value, onChange, required, testid }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-[0.18em] text-[#6B7280] mb-2">{label}{required && " *"}</label>
      <input
        type={type}
        value={value}
        onChange={onChange}
        required={required}
        data-testid={testid}
        className="w-full bg-[#F8F9FB] border border-gray-200 rounded-full px-5 py-3"
      />
    </div>
  );
}
